import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ColorStyleProps, Icon, Text } from '@quality24/design-system';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import LanguagePickerModal from '@/inpatient-patient-pwa/organisms/LanguagePickerModal';
import { useLanguages } from '@/inpatient-patient-pwa/contexts/language/useLanguages';

import styles from './LanguageButton.module.scss';

export interface Props {
  /** Color applied to both the globe icon and the language abbreviation. */
  color?: ColorStyleProps;
  className?: string;
}

/**
 * Button that opens the language picker. Displays the globe icon alongside the
 * abbreviation of the currently selected language (e.g. "PT", "EN", "ES").
 *
 * The picker only lists the languages the hospital enabled, taken from
 * `LanguageProvider`. Renders nothing when the hospital enabled no language
 * — there is no choice to offer, and the app stays on pt-BR.
 */
const LanguageButton: React.FunctionComponent<Props> = ({
  color = 'white',
  className,
}) => {
  const { t, i18n } = useTranslation();
  const { options } = useLanguages();
  const [open, setOpen] = React.useState(false);

  const languageAbbreviation = i18n.language.split('-')[0].toUpperCase();

  if (!options.length) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={classNames(
          'mb-0 p-0 border-0',
          styles['language-button'],
          className,
        )}
        aria-label={t('language.selector.open')}
      >
        <Icon icon={faGlobe} color={color} size="md" />
        <Text as="span" color={color}>
          {languageAbbreviation}
        </Text>
      </button>

      <LanguagePickerModal
        show={open}
        onHide={() => setOpen(false)}
        options={options}
      />
    </>
  );
};

export default LanguageButton;
